import { useState, useEffect, useCallback } from 'react';
import { syncManager } from '../services/syncManager';
import { cacheManager, formatCacheAge } from '../services/cacheManager';

// Hook for offline sync status
export function useOfflineSync() {
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingActions, setPendingActions] = useState([]);

  useEffect(() => {
    // Initial load
    loadPendingCount();

    // Subscribe to sync manager updates
    const unsubscribe = syncManager.subscribe(() => {
      loadPendingCount();
      setIsSyncing(syncManager.isSyncing);
    });

    // Online/offline listeners
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadPendingCount = async () => {
    const count = await syncManager.getPendingCount();
    setPendingCount(count);
    
    const actions = await syncManager.getPendingActions();
    setPendingActions(actions);
  };

  const syncNow = useCallback(async () => {
    if (!isOnline) return null;
    return syncManager.syncPendingActions();
  }, [isOnline]);

  const queueAction = useCallback(async (type, data, endpoint, method) => {
    return syncManager.queueAction(type, data, endpoint, method);
  }, []);

  return {
    pendingCount,
    pendingActions,
    isSyncing,
    isOnline,
    syncNow,
    queueAction,
    // Helper methods
    queueInventoryScan: syncManager.queueInventoryScan.bind(syncManager),
    queueFurnitureCreate: syncManager.queueFurnitureCreate.bind(syncManager),
    queueMovementCreate: syncManager.queueMovementCreate.bind(syncManager)
  };
}

// Hook for cache management
export function useCache() {
  const [cacheFreshness, setCacheFreshness] = useState({});
  const [isPreCaching, setIsPreCaching] = useState(false);
  const [cacheProgress, setCacheProgress] = useState(null);

  useEffect(() => {
    loadCacheFreshness();
    
    const unsubscribe = cacheManager.subscribe((status) => {
      setIsPreCaching(status.isPreCaching);
    });

    return unsubscribe;
  }, []);

  const loadCacheFreshness = async () => {
    const freshness = await cacheManager.getCacheFreshness();
    setCacheFreshness(freshness);
  };

  const preCacheAll = useCallback(async () => {
    setIsPreCaching(true);
    setCacheProgress({ completed: 0, total: 0 });
    
    await cacheManager.preCacheAll((progress) => {
      setCacheProgress(progress);
    });
    
    setIsPreCaching(false);
    setCacheProgress(null);
    await loadCacheFreshness();
  }, []);

  const preCacheWithFilter = useCallback(async (filters = {}) => {
    setIsPreCaching(true);
    setCacheProgress({ completed: 0, total: 0 });
    
    await cacheManager.preCacheWithFilter(filters, (progress) => {
      setCacheProgress(progress);
    });
    
    setIsPreCaching(false);
    setCacheProgress(null);
    await loadCacheFreshness();
  }, []);

  const getWithCache = useCallback(async (endpoint, fetcher) => {
    const result = await cacheManager.getWithCache(endpoint, fetcher);
    await loadCacheFreshness();
    return result;
  }, []);

  const clearCache = useCallback(async () => {
    await cacheManager.clearAll();
    setCacheFreshness({});
  }, []);

  const refreshStale = useCallback(async () => {
    await cacheManager.refreshStale();
    await loadCacheFreshness();
  }, []);

  return {
    cacheFreshness,
    isPreCaching,
    cacheProgress,
    preCacheAll,
    preCacheWithFilter,
    getWithCache,
    clearCache,
    refreshStale,
    formatCacheAge
  };
}

// Hook for cached API data with freshness indicator
export function useCachedData(endpoint, fetcher, options = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadData = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      // If forcing refresh and online, skip cache
      if (forceRefresh && navigator.onLine) {
        const response = await fetcher();
        await cacheManager.cacheEndpoint(endpoint, response.data);
        setData(response.data);
        setCacheInfo({
          fromCache: false,
          cachedAt: Date.now(),
          age: 'À l\'instant'
        });
      } else {
        // Use cache-aware fetching
        const result = await cacheManager.getWithCache(endpoint, fetcher);
        setData(result.data);
        setCacheInfo({
          fromCache: result.fromCache,
          cachedAt: result.cachedAt,
          age: result.age || 'À l\'instant'
        });
      }
    } catch (err) {
      console.error(`[useCachedData] Error loading ${endpoint}:`, err);
      setError(err.message || 'Erreur de chargement');
      
      // Try to get any cached data as last resort
      const cached = await cacheManager.getCachedData(endpoint);
      if (cached) {
        setData(cached.data);
        setCacheInfo({
          fromCache: true,
          cachedAt: cached.cachedAt,
          age: cached.age,
          isStale: true
        });
      }
    } finally {
      setLoading(false);
    }
  }, [endpoint, fetcher]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh when coming back online
  useEffect(() => {
    if (isOnline && cacheInfo?.fromCache && options.autoRefresh !== false) {
      loadData(true);
    }
  }, [isOnline]);

  return {
    data,
    loading,
    error,
    cacheInfo,
    isOnline,
    refresh: () => loadData(true),
    reload: () => loadData(false)
  };
}

export default { useOfflineSync, useCache, useCachedData };
